import { useState } from "react";
import { ChevronDown, Loader2, X } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export interface CheckboxMultiSelectOption {
  id: string;
  label: string;
  /** Shown after the label, e.g. a doctor's role. */
  detail?: string | null;
}

interface CheckboxMultiSelectProps {
  id?: string;
  options: CheckboxMultiSelectOption[];
  value: string[];
  onChange: (ids: string[]) => void;
  placeholder: string;
  /** Trigger text once something is selected, e.g. "2 doctors selected". */
  selectedText: (count: number) => string;
  emptyText: string;
  loading?: boolean;
  loadingText?: string;
  disabled?: boolean;
  invalid?: boolean;
  /** Accessible label for a tag's remove button. */
  removeLabel: (label: string) => string;
  triggerClassName?: string;
}

const optionText = (option: CheckboxMultiSelectOption) =>
  option.detail ? `${option.label} - ${option.detail}` : option.label;

/**
 * Select-style dropdown with a checkbox per option, so several can be picked
 * without it closing; selections are listed below as removable tags.
 */
export function CheckboxMultiSelect({
  id,
  options,
  value,
  onChange,
  placeholder,
  selectedText,
  emptyText,
  loading = false,
  loadingText,
  disabled = false,
  invalid = false,
  removeLabel,
  triggerClassName,
}: CheckboxMultiSelectProps) {
  const [open, setOpen] = useState(false);

  // Keep selections whose option hasn't loaded (yet) out of the tag list.
  const selectedOptions = value
    .map((selectedId) => options.find((option) => option.id === selectedId))
    .filter((option): option is CheckboxMultiSelectOption => option !== undefined);

  const toggle = (optionId: string, checked: boolean) => {
    onChange(
      checked
        ? [...value.filter((selectedId) => selectedId !== optionId), optionId]
        : value.filter((selectedId) => selectedId !== optionId),
    );
  };

  return (
    <div className="space-y-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            id={id}
            type="button"
            disabled={disabled}
            aria-invalid={invalid}
            className={cn(
              "flex h-10 w-full cursor-pointer items-center justify-between gap-2 rounded-lg border border-input bg-transparent px-2.5 text-left text-sm outline-none transition-colors focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50",
              invalid && "border-destructive",
              triggerClassName,
            )}
          >
            <span className={cn("truncate", value.length === 0 && "text-muted-foreground")}>
              {selectedOptions.length > 0 ? selectedText(selectedOptions.length) : placeholder}
            </span>
            <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="max-h-64 w-(--radix-popover-trigger-width) gap-0 overflow-y-auto p-1"
        >
          {loading ? (
            <div className="flex items-center gap-2 px-2 py-2 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              {loadingText}
            </div>
          ) : options.length === 0 ? (
            <p className="px-2 py-2 text-sm text-gray-500">{emptyText}</p>
          ) : (
            options.map((option) => {
              const checkboxId = `${id ?? "multi-select"}-${option.id}`;
              const checked = value.includes(option.id);
              return (
                <label
                  key={option.id}
                  htmlFor={checkboxId}
                  className="flex min-h-10 cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 text-sm hover:bg-gray-50"
                >
                  <Checkbox
                    id={checkboxId}
                    checked={checked}
                    onCheckedChange={(next) => toggle(option.id, next === true)}
                  />
                  <span className="break-words">{optionText(option)}</span>
                </label>
              );
            })
          )}
        </PopoverContent>
      </Popover>

      {selectedOptions.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedOptions.map((option) => (
            <span
              key={option.id}
              className="flex max-w-full items-center gap-1 rounded-md bg-gray-100 px-2 py-1 text-sm break-all"
            >
              {optionText(option)}
              <button
                type="button"
                className="cursor-pointer text-gray-500 hover:text-gray-900 disabled:cursor-not-allowed"
                aria-label={removeLabel(option.label)}
                disabled={disabled}
                onClick={() => toggle(option.id, false)}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
